import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Prescription } from '../../types';
import { Pill, CheckCircle, XCircle, Printer, Check, X, FileText } from 'lucide-react';
export const PharmacyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  // Use ID pattern for live updates
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Local state to track dispensing before saving
  const [dispenseStatus, setDispenseStatus] = useState<Record<string, 'Dispensed' | 'Not Dispensed'>>({});
  // State for showing the prescription preview modal
  const [showPrescriptionPreview, setShowPrescriptionPreview] = useState(false);
  const pendingEncounters = encounters.filter(e => e.stage === 'PHARMACY');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    const initialStatus: Record<string, 'Dispensed' | 'Not Dispensed'> = {};
    encounter.prescriptions.forEach(rx => {
      if (rx.status !== 'Pending') {
        initialStatus[rx.id] = rx.status;
      }
    });
    setDispenseStatus(initialStatus);
    setShowPrescriptionPreview(false);
  };
  const handleToggleStatus = (rxId: string, status: 'Dispensed' | 'Not Dispensed') => {
    setDispenseStatus(prev => ({
      ...prev,
      [rxId]: status
    }));
  };
  const handleCompleteDispensing = () => {
    if (!selectedEncounter || !currentUser) return;
    // Check if all drugs have a status
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    if (!allProcessed) {
      alert('Please mark all drugs as Dispensed or Not Available.');
      return;
    }
    const updatedPrescriptions = selectedEncounter.prescriptions.map(rx => ({
      ...rx,
      status: dispenseStatus[rx.id],
      dispensedAt: new Date().toISOString(),
      dispensedBy: currentUser.id,
      pharmacistName: currentUser.fullName
    }));
    const hasUndispensed = updatedPrescriptions.some(rx => rx.status === 'Not Dispensed');
    updateEncounter(selectedEncounter.id, {
      prescriptions: updatedPrescriptions,
      stage: 'CLINICIAN'
    });
    alert(hasUndispensed ? 'Dispensing completed. Report sent back to Clinician for review (some drugs were not available).' : 'Dispensing completed. Report sent back to Clinician.');
    setSelectedEncounterId(null);
    setDispenseStatus({});
    setShowPrescriptionPreview(false);
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-1047">Patient not found</div>;
    const notDispensedDrugs = selectedEncounter.prescriptions.filter(rx => dispenseStatus[rx.id] === 'Not Dispensed');
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    // If showing the prescription preview
    if (showPrescriptionPreview) {
      return <div className="space-y-6 pb-20" data-id="element-1048">
          <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1049">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1050">
              Prescription Preview
            </h1>
            <div className="flex space-x-2" data-id="element-1051">
              <Button variant="outline" onClick={() => setShowPrescriptionPreview(false)} data-id="element-1052">
                Back to Dispensing
              </Button>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-id="element-1053">
                <Printer className="w-4 h-4 mr-2" data-id="element-1054" /> Print Prescription
              </Button>
            </div>
          </div>

          {/* A4 Prescription Form Container */}
          <div className="flex justify-center print:block print:m-0" data-id="element-1055">
            <div className="bg-white shadow-lg print:shadow-none w-full max-w-[210mm] min-h-[297mm] p-10 print:p-0 print:w-full print:max-w-none border border-gray-200 print:border-none" data-id="element-1056">
              {/* Form Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-1057">
                <div className="flex items-center" data-id="element-1058">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-1059" />
                  <div data-id="element-1060">
                    <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-1061">
                      BMED CARE
                    </h1>
                    <p className="text-sm italic text-gray-600" data-id="element-1062">
                      Maternity Care Made Better
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-1063">
                  <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-1064">
                    PRESCRIPTION FORM
                  </p>
                  <p className="text-sm" data-id="element-1065">
                    <span className="font-semibold" data-id="element-1066">Date:</span>{' '}
                    {new Date().toLocaleDateString()}
                  </p>
                  <p className="text-sm" data-id="element-1067">
                    <span className="font-semibold" data-id="element-1068">File No:</span>{' '}
                    {patient.bmcFileNumber}
                  </p>
                </div>
              </div>

              {/* Patient Details */}
              <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-1069">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1070">
                  <div className="col-span-8" data-id="element-1071">
                    <span className="font-semibold" data-id="element-1072">Patient Name:</span>{' '}
                    {patient.firstName} {patient.lastName}
                  </div>
                  <div className="col-span-2" data-id="element-1073">
                    <span className="font-semibold" data-id="element-1074">Age:</span> {patient.age}
                  </div>
                  <div className="col-span-2" data-id="element-1075">
                    <span className="font-semibold" data-id="element-1076">Sex:</span> {patient.sex}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1077">
                  <div className="col-span-12" data-id="element-1078">
                    <span className="font-semibold" data-id="element-1079">Allergies:</span>{' '}
                    <span className="text-red-600 font-bold" data-id="element-1080">
                      {patient.allergies}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2" data-id="element-1081">
                  <div className="col-span-12" data-id="element-1082">
                    <span className="font-semibold" data-id="element-1083">
                      Prescribing Clinician:
                    </span>{' '}
                    {selectedEncounter.lastAttendingClinician}
                  </div>
                </div>
              </div>

              {/* Drugs Table */}
              <div className="mb-12" data-id="element-1084">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-1085">
                  Medication to be Sourced Externally
                </h3>
                <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-1086">
                  <thead data-id="element-1087">
                    <tr className="bg-gray-100" data-id="element-1088">
                      <th className="border border-black p-2 w-12" data-id="element-1089">No.</th>
                      <th className="border border-black p-2 text-left" data-id="element-1090">
                        Drug Name
                      </th>
                      <th className="border border-black p-2" data-id="element-1091">Dose</th>
                      <th className="border border-black p-2" data-id="element-1092">Route</th>
                      <th className="border border-black p-2" data-id="element-1093">Frequency</th>
                      <th className="border border-black p-2" data-id="element-1094">Days</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1095">
                    {notDispensedDrugs.map((rx, i) => <tr key={rx.id} data-id="element-1096">
                        <td className="border border-black p-3 font-medium" data-id="element-1097">
                          {i + 1}
                        </td>
                        <td className="border border-black p-3 text-left font-bold" data-id="element-1098">
                          {rx.drugName}
                        </td>
                        <td className="border border-black p-3" data-id="element-1099">{rx.dose}</td>
                        <td className="border border-black p-3" data-id="element-1100">{rx.route}</td>
                        <td className="border border-black p-3" data-id="element-1101">
                          {rx.frequency}
                        </td>
                        <td className="border border-black p-3" data-id="element-1102">{rx.days}</td>
                      </tr>)}
                    {/* Empty rows for padding if few drugs */}
                    {Array.from({
                    length: Math.max(0, 5 - notDispensedDrugs.length)
                  }).map((_, i) => <tr key={`empty-${i}`} data-id="element-1103">
                        <td className="border border-black p-4" data-id="element-1104"></td>
                        <td className="border border-black p-4" data-id="element-1105"></td>
                        <td className="border border-black p-4" data-id="element-1106"></td>
                        <td className="border border-black p-4" data-id="element-1107"></td>
                        <td className="border border-black p-4" data-id="element-1108"></td>
                        <td className="border border-black p-4" data-id="element-1109"></td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Footer Signatures */}
              <div className="mt-auto pt-10" data-id="element-1110">
                <div className="flex justify-between items-end" data-id="element-1111">
                  <div className="w-64" data-id="element-1112">
                    <p className="text-sm font-semibold mb-8" data-id="element-1113">
                      Pharmacist Signature:
                    </p>
                    <div className="border-b border-black" data-id="element-1114"></div>
                    <p className="text-sm mt-2 font-medium" data-id="element-1115">
                      {currentUser?.fullName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1116">
                      BMED Pharmacy Dept
                    </p>
                  </div>
                  <div className="w-64" data-id="element-1117">
                    <p className="text-sm font-semibold mb-8" data-id="element-1118">
                      Official Stamp:
                    </p>
                    <div className="border border-black h-24 w-24 rounded-full mx-auto opacity-20" data-id="element-1119"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>;
    }
    // Standard Dispensing View
    return <div className="space-y-6 pb-20 print:hidden" data-id="element-1120">
        <div className="flex justify-between items-center sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1121">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1122">
            Dispense Medication
          </h1>
          <div className="flex space-x-2" data-id="element-1123">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-1124">
              Back to Queue
            </Button>
            {notDispensedDrugs.length > 0 && <Button variant="outline" onClick={() => setShowPrescriptionPreview(true)} className="border-blue-600 text-blue-600 hover:bg-blue-50" data-id="element-1125">
                <FileText className="w-4 h-4 mr-2" data-id="element-1126" /> Generate Prescription
              </Button>}
            <Button onClick={handleCompleteDispensing} disabled={!allProcessed} className={`${allProcessed ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-400 cursor-not-allowed'} text-white`} data-id="element-1127">
              <Check className="w-5 h-5 mr-2" data-id="element-1128" /> Complete Dispensing
            </Button>
          </div>
        </div>

        {/* Medical Form Style Container */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-1129">
          {/* Patient Header */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-1130">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1131">
              <div className="col-span-8" data-id="element-1132">
                <span className="font-semibold" data-id="element-1133">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-1134">
                <span className="font-semibold" data-id="element-1135">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-1136">
                <span className="font-semibold" data-id="element-1137">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1138">
              <div className="col-span-6" data-id="element-1139">
                <span className="font-semibold" data-id="element-1140">File No:</span>{' '}
                {patient.bmcFileNumber}
              </div>
              <div className="col-span-6" data-id="element-1141">
                <span className="font-semibold" data-id="element-1142">Prescribing Clinician:</span>{' '}
                {selectedEncounter.lastAttendingClinician}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-1143">
              <div className="col-span-12" data-id="element-1144">
                <span className="font-semibold" data-id="element-1145">Allergies:</span>{' '}
                <span className="text-red-600 font-bold" data-id="element-1146">
                  {patient.allergies}
                </span>
              </div>
            </div>
          </div>

          {/* DRUG TREATMENT / PRESCRIPTION */}
          <div className="border-2 border-black" data-id="element-1147">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1148">
              Drug Treatment / Prescription
            </h3>
            <table className="w-full border-collapse text-sm text-center" data-id="element-1149">
              <thead data-id="element-1150">
                <tr className="bg-gray-100" data-id="element-1151">
                  <th className="border border-black p-2 w-10" data-id="element-1152">No.</th>
                  <th className="border border-black p-2 text-left" data-id="element-1153">
                    Drug Name
                  </th>
                  <th className="border border-black p-2" data-id="element-1154">Dose</th>
                  <th className="border border-black p-2" data-id="element-1155">Route</th>
                  <th className="border border-black p-2" data-id="element-1156">Frequency</th>
                  <th className="border border-black p-2" data-id="element-1157">Days</th>
                  <th className="border border-black p-2 w-64" data-id="element-1158">
                    Dispense Status
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1159">
                {selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-50' : dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-50' : ''} data-id="element-1160">
                    <td className="border border-black p-2 font-medium" data-id="element-1161">
                      {i + 1}
                    </td>
                    <td className="border border-black p-2 text-left font-bold" data-id="element-1162">
                      {rx.drugName}
                    </td>
                    <td className="border border-black p-2" data-id="element-1163">{rx.dose}</td>
                    <td className="border border-black p-2" data-id="element-1164">{rx.route}</td>
                    <td className="border border-black p-2" data-id="element-1165">{rx.frequency}</td>
                    <td className="border border-black p-2" data-id="element-1166">{rx.days}</td>
                    <td className="border border-black p-2" data-id="element-1167">
                      <div className="flex space-x-1 justify-center" data-id="element-1168">
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Dispensed')} data-id="element-1169">
                          <CheckCircle className="w-3 h-3 mr-1" data-id="element-1170" /> Dispensed
                        </button>
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Not Dispensed')} data-id="element-1171">
                          <XCircle className="w-3 h-3 mr-1" data-id="element-1172" /> Not Avail
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* NAME OF MEDICAL PRACTITIONER */}
          <div className="border-2 border-black p-4 mt-6" data-id="element-1173">
            <p className="font-bold text-sm" data-id="element-1174">
              Dispensing Pharmacist:{' '}
              <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-1175">
                {currentUser?.fullName || ''}
              </span>
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-1176">
      <div className="flex justify-between items-center" data-id="element-1177">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1178">Pharmacy Dashboard</h1>
        <Badge variant="success" className="text-sm px-3 py-1 bg-teal-100 text-teal-800" data-id="element-1179">
          {pendingEncounters.length} Prescriptions Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1180">
        <ul className="divide-y divide-gray-200" data-id="element-1181">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1182">
                  <div className="flex items-center" data-id="element-1183">
                    <div className="bg-teal-100 p-2 rounded-full mr-4" data-id="element-1184">
                      <Pill className="h-6 w-6 text-teal-600" data-id="element-1185" />
                    </div>
                    <div data-id="element-1186">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1187">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1188">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1189">
                        {encounter.prescriptions.length} items prescribed by{' '}
                        {encounter.lastAttendingClinician}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700 text-white" data-id="element-1190">
                    View Prescription
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1191">
              <Pill className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1192" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1193">
                No pending prescriptions
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};